/**
 * Helper class for chunk buffer.
 * @private
 */
export class _PdfChunkBuffer {
    /**
     * Initialize an instance of `_PdfChunkBuffer` class.
     * @private
     */
    constructor(chunkSize = 1048576) {
        /**
         * Specifies the `chunks`.
         * @private
         */
        this.chunks = [];
        /**
         * Specifies the `offset`.
         * @private
         */
        this.offset = 0;
        /**
         * Specifies the `committed`.
         * @private
         */
        this.committed = 0;
        this.chunkSize = chunkSize;
        this.current = new Uint8Array(this.chunkSize);
        this.chunks.push(this.current);
    }
    /**
     * Gets the `length`.
     * @private
     */
    get length() {
        return this.committed + this.offset;
    }
    /**
     * Grows the buffer by allocating a new chunk.
     * @private
     */
    grow() {
        this.committed += this.offset;
        this.current = new Uint8Array(this.chunkSize);
        this.chunks.push(this.current);
        this.offset = 0;
    }
    /**
     * Writes ASCII string as bytes (each charCodeAt masked to 0..255).
     * @private
     */
    writeAscii(str) {
        let idx = 0;
        while (idx < str.length) {
            if (this.offset >= this.current.byteLength) {
                this.grow();
            }
            const space = this.current.byteLength - this.offset;
            const toWrite = Math.min(space, str.length - idx);
            for (let i = 0; i < toWrite; i++) {
                this.current[this.offset + i] = str.charCodeAt(idx + i) & 0xFF;
            }
            this.offset += toWrite;
            idx += toWrite;
        }
    }
    /**
     * Converts to `Uint8Array`.
     * @private
     */
    toUint8Array() {
        const total = this.length;
        const out = new Uint8Array(total);
        let pos = 0;
        const lastIdx = this.chunks.length - 1;
        for (let i = 0; i <= lastIdx; i++) {
            const chunk = this.chunks[i];
            if (i === lastIdx) {
                out.set(chunk.subarray(0, this.offset), pos);
                pos += this.offset;
            }
            else {
                out.set(chunk, pos);
                pos += chunk.byteLength;
            }
        }
        return out;
    }
    /**
     * Destroys the array buffer.
     * @private
     */
    destroy() {
        this.chunks = [];
        this.current = undefined;
        this.offset = 0;
        this.committed = 0;
    }
}
/**
 * Used to `write a string` into output file.
 * @private
 */
export class PdfWriter {
    constructor(stream) {
        /**
         * Specifies the `byteCountForStreamWriter`.
         * @private
         */
        this.byteCountForStreamWriter = 0;
        this.streamWriter = stream;
    }
    /**
     * Gets and Sets the `document`.
     * @private
     */
    get document() {
        return this.pdfDocument;
    }
    set document(value) {
        this.pdfDocument = value;
    }
    /**
     * Gets the `position`.
     * @private
     */
    get position() {
        if (this.streamWriter instanceof PdfWriterHelper) {
            return this.streamWriter.buffer.size;
        }
        // For StreamWriter, we keep an internal count based on written characters
        return this.byteCountForStreamWriter;
    }
    /**
     * Gets  the `length` of the stream'.
     * @private
     */
    get length() {
        return this.position;
    }
    /**
     * Gets the `stream`.
     * @private
     */
    get stream() {
        return this.streamWriter;
    }
    /**
     * `Writes the specified data`.
     * @private
     */
    write(data) {
        if (typeof data === 'number') {
            data = String.fromCharCode(data);
        }
        if (this.streamWriter instanceof PdfWriterHelper) {
            this.streamWriter.write(data);
        }
        else {
            this.streamWriter.write(data);
            this.byteCountForStreamWriter += data.length;
        }
    }
}
/**
 * Helper class for PDF writer.
 * @private
 */
export class PdfWriterHelper {
    /**
     * Initialize an instance of `PdfWriterHelper` class.
     * @private
     */
    constructor(chunkSize = 1048576) {
        this.buffer = new PdfArrayBuffer(chunkSize);
    }
    /**
     * Writes the specified data.
     * @private
     */
    write(data) {
        this.buffer.write(data);
    }
    /**
     * Destroy the array buffer.
     * @private
     */
    destroy() {
        if (this.buffer) {
            this.buffer.destroy();
            this.buffer = undefined;
        }
    }
}
/**
 * Helper class for PDF writer.
 * @private
 */
export class PdfArrayBuffer {
    /**
     * Initialize an instance of `PdfArrayBuffer` class.
     * @private
     */
    constructor(chunkSize = 1048576) {
        this.buf = new _PdfChunkBuffer(chunkSize);
    }
    /**
     * Gets the `size`.
     * @private
     */
    get size() {
        return this.buf.length;
    }
    /**
     * Writes the specified data.
     * @private
     */
    write(value) {
        this.buf.writeAscii(value);
    }
    /**
     * Converts to `Uint8Array`.
     * @private
     */
    toUint8Array() {
        return this.buf.toUint8Array();
    }
    /**
     * Destroys the buffer.
     * @private
     */
    destroy() {
        if (this.buf) {
            this.buf.destroy();
            this.buf = undefined;
        }
    }
}
